// // AnalyticsPage.jsx
// // Created: 06-04-2026
// // Full analytics dashboard for the real estate CRM portal.
// // Data sourced from: /leads/all, /properties, /deals, /payments
// // Charts built with Recharts (no extra install needed).
// // Sections:
// //   1. KPI summary cards
// //   2. Deal pipeline by stage (funnel bar)
// //   3. Lead sources (horizontal bar)
// //   4. Lead status distribution (donut)
// //   5. Lead interest level split
// //   6. Payment status & method breakdown
// //   7. Monthly revenue trend
// //   8. Property category & status split
// //   9. Win rate & deal health metrics

// import React, { useEffect, useState, useMemo } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//   BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
//   PieChart, Pie, Cell, Legend, LineChart, Line, AreaChart, Area,
// } from "recharts";
// import {
//   TrendingUp, Users, Home, CreditCard,
//   CheckCircle, Clock, AlertTriangle, Award,
//   ArrowUpRight, Percent, RefreshCw,
// } from "lucide-react";
// import { motion } from "framer-motion";
// import api from "../api/api";
// import { useAuth } from "../api/authContext";
// import "../css/analytics/analyticspage.css";

// /* ─────────────────── COLOUR PALETTE ─────────────────── */
// const C = {
//   purple:  "#7C5CFC",
//   teal:    "#14B8A6",
//   amber:   "#F59E0B",
//   rose:    "#F43F5E",
//   blue:    "#3B82F6",
//   green:   "#22C55E",
//   slate:   "#64748B",
//   indigo:  "#6366F1",
//   orange:  "#F97316",
//   cyan:    "#06B6D4",
// };

// const STAGE_COLORS = {
//   negotiation:     C.blue,
//   documentation:   C.indigo,
//   payment_pending: C.amber,
//   closed_won:      C.green,
//   closed_lost:     C.rose,
// };

// const STATUS_COLORS_PAYMENT = {
//   Received: C.green,
//   Pending:  C.amber,
//   Overdue:  C.rose,
// };

// const STATUS_COLORS_LEAD = {
//   new:        C.blue,
//   contacted:  C.cyan,
//   interested: C.indigo,
//   follow_up:  C.amber,
//   closed:     C.green,
//   lost:       C.rose,
// };

// const INTEREST_COLORS = {
//   hot:  C.rose,
//   warm: C.amber,
//   cold: C.blue,
// };

// const SOURCE_COLORS = [C.purple, C.teal, C.blue, C.amber, C.rose, C.green, C.cyan];

// /* ─────────────────── HELPERS ─────────────────── */
// const fmt = (n) =>
//   n >= 10_000_000
//     ? `₹${(n / 10_000_000).toFixed(2)}Cr`
//     : n >= 100_000
//     ? `₹${(n / 100_000).toFixed(1)}L`
//     : n >= 1000
//     ? `₹${(n / 1000).toFixed(0)}K`
//     : `₹${n}`;

// const pct = (num, den) => (den === 0 ? 0 : Math.round((num / den) * 100));

// const countBy = (arr, key) =>
//   arr.reduce((acc, item) => {
//     const val = item[key] || "unknown";
//     acc[val] = (acc[val] || 0) + 1;
//     return acc;
//   }, {});

// const toChartArr = (obj, labelMap = {}) =>
//   Object.entries(obj).map(([k, v]) => ({
//     name: labelMap[k] || k.replace(/_/g, " "),
//     value: v,
//   }));

// /* ─────────────────── SUB-COMPONENTS ─────────────────── */

// function KpiCard({ icon: Icon, label, value, sub, color, delay = 0 }) {
//   return (
//     <motion.div
//       className="kpi-card"
//       initial={{ opacity: 0, y: 18 }}
//       animate={{ opacity: 1, y: 0 }}
//       transition={{ delay, duration: 0.35 }}
//     >
//       <div className="kpi-icon" style={{ background: color + "1a", color }}>
//         <Icon size={20} />
//       </div>
//       <div className="kpi-body">
//         <span className="kpi-label">{label}</span>
//         <span className="kpi-value">{value}</span>
//         {sub && <span className="kpi-sub">{sub}</span>}
//       </div>
//     </motion.div>
//   );
// }

// function ChartCard({ title, children, className = "" }) {
//   return (
//     <motion.div
//       className={`chart-card ${className}`}
//       initial={{ opacity: 0, y: 16 }}
//       animate={{ opacity: 1, y: 0 }}
//       transition={{ duration: 0.4 }}
//     >
//       <h3 className="chart-title">{title}</h3>
//       {children}
//     </motion.div>
//   );
// }

// const CustomTooltip = ({ active, payload, label, formatter }) => {
//   if (!active || !payload?.length) return null;
//   return (
//     <div className="chart-tooltip">
//       {label && <p className="tt-label">{label}</p>}
//       {payload.map((p, i) => (
//         <p key={i} style={{ color: p.color || p.fill }}>
//           {p.name}: <strong>{formatter ? formatter(p.value) : p.value}</strong>
//         </p>
//       ))}
//     </div>
//   );
// };

// const CustomPieLabel = ({ cx, cy, midAngle, outerRadius, percent }) => {
//   if (percent < 0.05) return null;
//   const r  = outerRadius + 18;
//   const x  = cx + r * Math.cos((-midAngle * Math.PI) / 180);
//   const y  = cy + r * Math.sin((-midAngle * Math.PI) / 180);
//   return (
//     <text x={x} y={y} fill="var(--text-secondary)" textAnchor="middle" fontSize={11}>
//       {`${(percent * 100).toFixed(0)}%`}
//     </text>
//   );
// };

// /* ─────────────────── MAIN COMPONENT ─────────────────── */
// export default function AnalyticsPage() {
//   const navigate   = useNavigate();
//   const { token }  = useAuth();

//   const [leads,      setLeads]      = useState([]);
//   const [properties, setProperties] = useState([]);
//   const [deals,      setDeals]      = useState([]);
//   const [payments,   setPayments]   = useState([]);
//   const [loading,    setLoading]    = useState(true);
//   const [lastFetch,  setLastFetch]  = useState(null);

//   /* ── FETCH ── */
//   const fetchAll = async () => {
//     setLoading(true);
//     try {
//       const [lRes, pRes, dRes, pyRes] = await Promise.all([
//         api.get("/leads/all"),
//         api.get("/properties"),
//         api.get("/deals"),
//         api.get("/payments"),
//       ]);
//       setLeads(      Array.isArray(lRes.data)  ? lRes.data  : lRes.data.data  || []);
//       setProperties( Array.isArray(pRes.data)  ? pRes.data  : pRes.data.data  || []);
//       setDeals(      Array.isArray(dRes.data)  ? dRes.data  : dRes.data.data  || []);
//       setPayments(   Array.isArray(pyRes.data) ? pyRes.data : pyRes.data.data || []);
//       setLastFetch(new Date());
//     } catch (err) {
//       console.error("Analytics fetch error", err);
//       if (err.response?.status === 401) navigate("/login");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (!token) { navigate("/login"); return; }
//     fetchAll();
//   }, [token]);

//   /* ─────────────── DERIVED METRICS ─────────────── */
//   const metrics = useMemo(() => {
//     /* KPIs */
//     const totalPipeline    = deals.reduce((s, d) => s + (d.dealValue || 0), 0);
//     const totalReceived    = payments.filter(p => p.status === "Received").reduce((s, p) => s + p.amount, 0);
//     const totalCommission  = deals.reduce((s, d) => s + (d.commissionAmout || 0), 0);
//     const wonDeals         = deals.filter(d => d.stage === "closed_won");
//     const lostDeals        = deals.filter(d => d.stage === "closed_lost");
//     const winRate          = pct(wonDeals.length, wonDeals.length + lostDeals.length);
//     const avgDealValue     = deals.length ? Math.round(totalPipeline / deals.length) : 0;
//     const hotLeads         = leads.filter(l => l.interest_level === "hot").length;
//     const availableProps   = properties.filter(p => p.status === "available").length;

//     /* Deal pipeline */
//     const stageOrder = ["negotiation", "documentation", "payment_pending", "closed_won", "closed_lost"];
//     const stageLabelMap = {
//       negotiation: "Negotiation", documentation: "Documentation",
//       payment_pending: "Pmt Pending", closed_won: "Won", closed_lost: "Lost",
//     };
//     const stageCounts = countBy(deals, "stage");
//     const pipelineData = stageOrder.map(s => ({
//       name:  stageLabelMap[s],
//       value: stageCounts[s] || 0,
//       fill:  STAGE_COLORS[s],
//     }));

//     /* Lead sources */
//     const sourceCounts  = countBy(leads, "source");
//     const sourceData    = toChartArr(sourceCounts, {
//       website: "Website", facebook: "Social", google: "Google",
//       walkin: "Walk-in", referral: "Referral", whatsapp: "WhatsApp", other: "Other",
//     }).sort((a, b) => b.value - a.value);

//     /* Lead status */
//     const leadStatusCounts = countBy(leads, "status");
//     const leadStatusData   = toChartArr(leadStatusCounts, {
//       new: "New", contacted: "Contacted", interested: "Interested",
//       follow_up: "Follow-up", closed: "Closed", lost: "Lost",
//     });

//     /* Interest level */
//     const interestCounts = countBy(leads.filter(l => l.interest_level), "interest_level");
//     const interestData   = toChartArr(interestCounts, { hot: "Hot", warm: "Warm", cold: "Cold" });

//     /* Lead type */
//     const leadTypeCounts = countBy(leads.filter(l => l.lead_type), "lead_type");
//     const leadTypeData   = toChartArr(leadTypeCounts, { buyer: "Buyer", renter: "Tenant", seller: "Seller" });

//     /* Payment status */
//     const pyStatusCounts = countBy(payments, "status");
//     const pyStatusData   = toChartArr(pyStatusCounts);

//     /* Payment method */
//     const pyMethodCounts = countBy(payments, "paymentMethod");
//     const pyMethodData   = toChartArr(pyMethodCounts).sort((a, b) => b.value - a.value);

//     /* Monthly revenue (last 6 months) */
//     const now = new Date();
//     const monthlyMap = {};
//     for (let i = 5; i >= 0; i--) {
//       const d   = new Date(now.getFullYear(), now.getMonth() - i, 1);
//       const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
//       const label = d.toLocaleString("default", { month: "short", year: "2-digit" });
//       monthlyMap[key] = { name: label, received: 0, pending: 0 };
//     }
//     payments.forEach(p => {
//       const d   = new Date(p.paymentDate);
//       const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
//       if (!monthlyMap[key]) return;
//       if (p.status === "Received") monthlyMap[key].received += p.amount;
//       else if (p.status === "Pending") monthlyMap[key].pending += p.amount;
//     });
//     const monthlyData = Object.values(monthlyMap);

//     /* Property category */
//     const propCatCounts = countBy(properties, "propertyCategory");
//     const propCatData   = toChartArr(propCatCounts, { residential: "Residential", commercial: "Commercial" });

//     /* Property status */
//     const propStatusCounts = countBy(properties, "status");
//     const propStatusData   = toChartArr(propStatusCounts, {
//       available: "Available", under_offer: "Under Offer",
//       sold: "Sold", rented: "Rented", unavailable: "Unavailable",
//       under_construction: "Under Const.", other: "Other",
//     }).sort((a, b) => b.value - a.value);

//     /* Property listing type */
//     const propListingCounts = countBy(properties, "listingType");
//     const propListingData   = toChartArr(propListingCounts, { sale: "For Sale", rent: "For Rent" });

//     /* Deal value by stage */
//     const stageValueMap = {};
//     deals.forEach(d => {
//       const s = stageLabelMap[d.stage] || d.stage;
//       stageValueMap[s] = (stageValueMap[s] || 0) + (d.dealValue || 0);
//     });
//     const stageValueData = stageOrder
//       .map(s => ({ name: stageLabelMap[s], value: stageValueMap[stageLabelMap[s]] || 0, fill: STAGE_COLORS[s] }))
//       .filter(d => d.value > 0);

//     return {
//       totalPipeline, totalReceived, totalCommission, winRate, avgDealValue,
//       hotLeads, availableProps, wonDeals: wonDeals.length, lostDeals: lostDeals.length,
//       pipelineData, sourceData, leadStatusData, interestData, leadTypeData,
//       pyStatusData, pyMethodData, monthlyData,
//       propCatData, propStatusData, propListingData, stageValueData,
//     };
//   }, [leads, properties, deals, payments]);

//   /* ─────────────── RENDER ─────────────── */
//   if (loading) {
//     return (
//       <div className="analytics-page">
//         <div className="analytics-loading">
//           <RefreshCw size={28} className="spin" />
//           <p>Loading analytics...</p>
//         </div>
//       </div>
//     );
//   }

//   const PIE_COLORS_STATUS   = ["#22C55E", "#F59E0B", "#F43F5E"];
//   const PIE_COLORS_INTEREST = [C.rose, C.amber, C.blue];
//   const PIE_COLORS_CAT      = [C.purple, C.teal];
//   const PIE_COLORS_LISTING  = [C.indigo, C.cyan];
//   const PIE_COLORS_LTYPE    = [C.blue, C.purple, C.teal];

//   return (
//     <div className="analytics-page">
//       <div className="analytics-container">

//         {/* ── Page Header ── */}
//         <div className="analytics-header">
//           <div>
//             <h1>Analytics</h1>
//             <p className="analytics-sub">
//               Live overview across leads, properties, deals &amp; payments
//             </p>
//           </div>
//           <button className="refresh-btn" onClick={fetchAll}>
//             <RefreshCw size={15} />
//             Refresh
//             {lastFetch && (
//               <span className="refresh-time">
//                 {lastFetch.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
//               </span>
//             )}
//           </button>
//         </div>

//         {/* ══════════════════════════════════
//             SECTION 1 — KPI CARDS
//         ══════════════════════════════════ */}
//         <div className="kpi-grid">
//           <KpiCard icon={TrendingUp}   label="Total Pipeline Value" value={fmt(metrics.totalPipeline)}  sub={`${deals.length} deals`}              color={C.purple} delay={0}    />
//           <KpiCard icon={CreditCard}   label="Revenue Received"     value={fmt(metrics.totalReceived)}  sub={`${payments.filter(p=>p.status==="Received").length} payments`} color={C.green}  delay={0.05} />
//           <KpiCard icon={Award}        label="Total Commission"      value={fmt(metrics.totalCommission)} sub="across all deals"                   color={C.amber}  delay={0.1}  />
//           <KpiCard icon={Percent}      label="Win Rate"              value={`${metrics.winRate}%`}       sub={`${metrics.wonDeals}W / ${metrics.lostDeals}L`} color={C.teal}  delay={0.15} />
//           <KpiCard icon={Users}        label="Total Leads"           value={leads.length}               sub={`${metrics.hotLeads} hot leads`}      color={C.rose}   delay={0.2}  />
//           <KpiCard icon={Home}         label="Listed Properties"     value={properties.length}          sub={`${metrics.availableProps} available`} color={C.blue}   delay={0.25} />
//           <KpiCard icon={CheckCircle}  label="Deals Won"             value={metrics.wonDeals}           sub={`Avg ${fmt(metrics.avgDealValue)}`}    color={C.green}  delay={0.3}  />
//           <KpiCard icon={AlertTriangle} label="Overdue Payments"     value={payments.filter(p=>p.status==="Overdue").length} sub="need attention" color={C.orange} delay={0.35} />
//         </div>

//         {/* ══════════════════════════════════
//             SECTION 2 — DEALS
//         ══════════════════════════════════ */}
//         <div className="section-label">Deal Pipeline</div>
//         <div className="charts-row charts-row-2">

//           {/* Deal count by stage */}
//           <ChartCard title="Deals by Stage">
//             <ResponsiveContainer width="100%" height={220}>
//               <BarChart data={metrics.pipelineData} barCategoryGap="30%">
//                 <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
//                 <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
//                 <YAxis tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} allowDecimals={false} />
//                 <Tooltip content={<CustomTooltip />} />
//                 <Bar dataKey="value" name="Deals" radius={[6, 6, 0, 0]}>
//                   {metrics.pipelineData.map((entry, i) => (
//                     <Cell key={i} fill={entry.fill} />
//                   ))}
//                 </Bar>
//               </BarChart>
//             </ResponsiveContainer>
//           </ChartCard>

//           {/* Deal value by stage */}
//           <ChartCard title="Deal Value by Stage (₹)">
//             <ResponsiveContainer width="100%" height={220}>
//               <BarChart data={metrics.stageValueData} barCategoryGap="30%">
//                 <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
//                 <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
//                 <YAxis tickFormatter={v => fmt(v)} tick={{ fontSize: 10, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
//                 <Tooltip content={<CustomTooltip formatter={fmt} />} />
//                 <Bar dataKey="value" name="Value" radius={[6, 6, 0, 0]}>
//                   {metrics.stageValueData.map((entry, i) => (
//                     <Cell key={i} fill={entry.fill} />
//                   ))}
//                 </Bar>
//               </BarChart>
//             </ResponsiveContainer>
//           </ChartCard>
//         </div>

//         {/* ══════════════════════════════════
//             SECTION 3 — PAYMENTS
//         ══════════════════════════════════ */}
//         <div className="section-label">Payments</div>
//         <div className="charts-row charts-row-3">

//           {/* Monthly revenue */}
//           <ChartCard title="Monthly Revenue (Last 6 Months)" className="span-2">
//             <ResponsiveContainer width="100%" height={220}>
//               <AreaChart data={metrics.monthlyData}>
//                 <defs>
//                   <linearGradient id="gReceived" x1="0" y1="0" x2="0" y2="1">
//                     <stop offset="5%"  stopColor={C.green} stopOpacity={0.25} />
//                     <stop offset="95%" stopColor={C.green} stopOpacity={0} />
//                   </linearGradient>
//                   <linearGradient id="gPending" x1="0" y1="0" x2="0" y2="1">
//                     <stop offset="5%"  stopColor={C.amber} stopOpacity={0.2} />
//                     <stop offset="95%" stopColor={C.amber} stopOpacity={0} />
//                   </linearGradient>
//                 </defs>
//                 <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
//                 <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
//                 <YAxis tickFormatter={v => fmt(v)} tick={{ fontSize: 10, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
//                 <Tooltip content={<CustomTooltip formatter={fmt} />} />
//                 <Legend wrapperStyle={{ fontSize: 12 }} />
//                 <Area type="monotone" dataKey="received" name="Received" stroke={C.green} fill="url(#gReceived)" strokeWidth={2} dot={{ r: 3, fill: C.green }} />
//                 <Area type="monotone" dataKey="pending"  name="Pending"  stroke={C.amber} fill="url(#gPending)"  strokeWidth={2} dot={{ r: 3, fill: C.amber }} />
//               </AreaChart>
//             </ResponsiveContainer>
//           </ChartCard>

//           {/* Payment status donut */}
//           <ChartCard title="Payment Status">
//             <ResponsiveContainer width="100%" height={220}>
//               <PieChart>
//                 <Pie data={metrics.pyStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
//                   dataKey="value" nameKey="name" labelLine={false} label={CustomPieLabel}>
//                   {metrics.pyStatusData.map((_, i) => (
//                     <Cell key={i} fill={PIE_COLORS_STATUS[i % PIE_COLORS_STATUS.length]} />
//                   ))}
//                 </Pie>
//                 <Tooltip content={<CustomTooltip />} />
//                 <Legend wrapperStyle={{ fontSize: 12 }} />
//               </PieChart>
//             </ResponsiveContainer>
//           </ChartCard>
//         </div>

//         {/* Payment methods bar */}
//         <div className="charts-row charts-row-1">
//           <ChartCard title="Payments by Method">
//             <ResponsiveContainer width="100%" height={180}>
//               <BarChart data={metrics.pyMethodData} layout="vertical" barCategoryGap="25%">
//                 <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
//                 <XAxis type="number" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} allowDecimals={false} />
//                 <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} width={90} />
//                 <Tooltip content={<CustomTooltip />} />
//                 <Bar dataKey="value" name="Payments" fill={C.indigo} radius={[0, 6, 6, 0]} />
//               </BarChart>
//             </ResponsiveContainer>
//           </ChartCard>
//         </div>

//         {/* ══════════════════════════════════
//             SECTION 4 — LEADS
//         ══════════════════════════════════ */}
//         <div className="section-label">Leads</div>
//         <div className="charts-row charts-row-3">

//           {/* Lead source horizontal bar */}
//           <ChartCard title="Lead Sources" className="span-2">
//             <ResponsiveContainer width="100%" height={220}>
//               <BarChart data={metrics.sourceData} layout="vertical" barCategoryGap="25%">
//                 <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
//                 <XAxis type="number" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} allowDecimals={false} />
//                 <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} width={70} />
//                 <Tooltip content={<CustomTooltip />} />
//                 <Bar dataKey="value" name="Leads" radius={[0, 6, 6, 0]}>
//                   {metrics.sourceData.map((_, i) => (
//                     <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
//                   ))}
//                 </Bar>
//               </BarChart>
//             </ResponsiveContainer>
//           </ChartCard>

//           {/* Lead interest level */}
//           <ChartCard title="Interest Level">
//             <ResponsiveContainer width="100%" height={220}>
//               <PieChart>
//                 <Pie data={metrics.interestData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
//                   dataKey="value" nameKey="name" labelLine={false} label={CustomPieLabel}>
//                   {metrics.interestData.map((entry, i) => (
//                     <Cell key={i} fill={INTEREST_COLORS[entry.name?.toLowerCase()] || PIE_COLORS_INTEREST[i]} />
//                   ))}
//                 </Pie>
//                 <Tooltip content={<CustomTooltip />} />
//                 <Legend wrapperStyle={{ fontSize: 12 }} />
//               </PieChart>
//             </ResponsiveContainer>
//           </ChartCard>
//         </div>

//         <div className="charts-row charts-row-2">

//           {/* Lead status donut */}
//           <ChartCard title="Lead Status">
//             <ResponsiveContainer width="100%" height={220}>
//               <PieChart>
//                 <Pie data={metrics.leadStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
//                   dataKey="value" nameKey="name" labelLine={false} label={CustomPieLabel}>
//                   {metrics.leadStatusData.map((entry, i) => (
//                     <Cell key={i} fill={STATUS_COLORS_LEAD[Object.keys(STATUS_COLORS_LEAD)[i]] || SOURCE_COLORS[i]} />
//                   ))}
//                 </Pie>
//                 <Tooltip content={<CustomTooltip />} />
//                 <Legend wrapperStyle={{ fontSize: 12 }} />
//               </PieChart>
//             </ResponsiveContainer>
//           </ChartCard>

//           {/* Lead type */}
//           <ChartCard title="Lead Type">
//             <ResponsiveContainer width="100%" height={220}>
//               <PieChart>
//                 <Pie data={metrics.leadTypeData} cx="50%" cy="50%" outerRadius={85}
//                   dataKey="value" nameKey="name" labelLine={false} label={CustomPieLabel}>
//                   {metrics.leadTypeData.map((_, i) => (
//                     <Cell key={i} fill={PIE_COLORS_LTYPE[i % PIE_COLORS_LTYPE.length]} />
//                   ))}
//                 </Pie>
//                 <Tooltip content={<CustomTooltip />} />
//                 <Legend wrapperStyle={{ fontSize: 12 }} />
//               </PieChart>
//             </ResponsiveContainer>
//           </ChartCard>
//         </div>

//         {/* ══════════════════════════════════
//             SECTION 5 — PROPERTIES
//         ══════════════════════════════════ */}
//         <div className="section-label">Properties</div>
//         <div className="charts-row charts-row-3">

//           {/* Property status bar */}
//           <ChartCard title="Property Status" className="span-2">
//             <ResponsiveContainer width="100%" height={220}>
//               <BarChart data={metrics.propStatusData} barCategoryGap="30%">
//                 <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
//                 <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
//                 <YAxis tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} allowDecimals={false} />
//                 <Tooltip content={<CustomTooltip />} />
//                 <Bar dataKey="value" name="Properties" fill={C.teal} radius={[6, 6, 0, 0]}>
//                   {metrics.propStatusData.map((_, i) => (
//                     <Cell key={i} fill={[C.green, C.amber, C.rose, C.cyan, C.slate, C.orange, C.blue][i % 7]} />
//                   ))}
//                 </Bar>
//               </BarChart>
//             </ResponsiveContainer>
//           </ChartCard>

//           {/* Property category donut */}
//           <ChartCard title="Category Split">
//             <ResponsiveContainer width="100%" height={220}>
//               <PieChart>
//                 <Pie data={metrics.propCatData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
//                   dataKey="value" nameKey="name" labelLine={false} label={CustomPieLabel}>
//                   {metrics.propCatData.map((_, i) => (
//                     <Cell key={i} fill={PIE_COLORS_CAT[i % PIE_COLORS_CAT.length]} />
//                   ))}
//                 </Pie>
//                 <Tooltip content={<CustomTooltip />} />
//                 <Legend wrapperStyle={{ fontSize: 12 }} />
//               </PieChart>
//             </ResponsiveContainer>
//           </ChartCard>
//         </div>

//         {/* Listing type */}
//         <div className="charts-row charts-row-1">
//           <ChartCard title="Listing Type (Sale vs Rent)">
//             <ResponsiveContainer width="100%" height={160}>
//               <BarChart data={metrics.propListingData} barCategoryGap="50%">
//                 <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
//                 <XAxis dataKey="name" tick={{ fontSize: 12, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
//                 <YAxis tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} allowDecimals={false} />
//                 <Tooltip content={<CustomTooltip />} />
//                 <Bar dataKey="value" name="Properties" radius={[8, 8, 0, 0]}>
//                   {metrics.propListingData.map((_, i) => (
//                     <Cell key={i} fill={PIE_COLORS_LISTING[i % PIE_COLORS_LISTING.length]} />
//                   ))}
//                 </Bar>
//               </BarChart>
//             </ResponsiveContainer>
//           </ChartCard>
//         </div>

//       </div>
//     </div>
//   );
// }
// AnalyticsPage.jsx
// Created: 06-04-2026
// Full analytics dashboard for the real estate CRM portal.
// Data sourced from: /leads/all, /properties, /deals, /payments
// Charts built with Recharts (no extra install needed).
// Sections:
//   1. KPI summary cards
//   2. Deal pipeline by stage (funnel bar)
//   3. Lead sources (horizontal bar)
//   4. Lead status distribution (donut)
//   5. Lead interest level split
//   6. Payment status & method breakdown
//   7. Monthly revenue trend
//   8. Property category & status split
//   9. Win rate & deal health metrics

import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, AreaChart, Area,
} from "recharts";
import {
  TrendingUp, Users, Home, CreditCard,
  CheckCircle, Clock, AlertTriangle, Award,
  ArrowUpRight, Percent, RefreshCw,
} from "lucide-react";
import { motion } from "framer-motion";
import api from "../api/api";
import { useAuth } from "../api/authContext";
import "../css/analytics/analyticspage.css";
import "../css/analytics/analyticsModern.css";

/* ─────────────────── COLOUR PALETTE ─────────────────── */
const C = {
  purple:  "#7C5CFC",
  teal:    "#14B8A6",
  amber:   "#F59E0B",
  rose:    "#F43F5E",
  blue:    "#3B82F6",
  green:   "#22C55E",
  slate:   "#64748B",
  indigo:  "#6366F1",
  orange:  "#F97316",
  cyan:    "#06B6D4",
};

const STAGE_COLORS = {
  negotiation:     C.blue,
  documentation:   C.indigo,
  payment_pending: C.amber,
  closed_won:      C.green,
  closed_lost:     C.rose,
};

const STATUS_COLORS_PAYMENT = {
  Received: C.green,
  Pending:  C.amber,
  Overdue:  C.rose,
};

const STATUS_COLORS_LEAD = {
  new:        C.blue,
  contacted:  C.cyan,
  interested: C.indigo,
  follow_up:  C.amber,
  closed:     C.green,
  lost:       C.rose,
};

const INTEREST_COLORS = {
  hot:  C.rose,
  warm: C.amber,
  cold: C.blue,
};

const SOURCE_COLORS = [C.purple, C.teal, C.blue, C.amber, C.rose, C.green, C.cyan];

/* ─────────────────── HELPERS ─────────────────── */
const fmt = (n) =>
  n >= 10_000_000
    ? `₹${(n / 10_000_000).toFixed(2)}Cr`
    : n >= 100_000
    ? `₹${(n / 100_000).toFixed(1)}L`
    : n >= 1000
    ? `₹${(n / 1000).toFixed(0)}K`
    : `₹${n}`;

const pct = (num, den) => (den === 0 ? 0 : Math.round((num / den) * 100));

const countBy = (arr, key) =>
  arr.reduce((acc, item) => {
    const raw = item[key];
    const val = (raw !== undefined && raw !== null && raw !== "") ? raw : "unknown";
    acc[val] = (acc[val] || 0) + 1;
    return acc;
  }, {});

const toChartArr = (obj, labelMap = {}) =>
  Object.entries(obj).map(([k, v]) => ({
    name: labelMap[k] || k.replace(/_/g, " "),
    value: v,
  }));

/* ─────────────────── SUB-COMPONENTS ─────────────────── */

function KpiCard({ icon: Icon, label, value, sub, color, delay = 0 }) {
  return (
    <motion.div
      className="kpi-card"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35 }}
    >
      <div className="kpi-icon" style={{ background: color + "1a", color }}>
        <Icon size={18} />
      </div>
      <div className="kpi-body">
        <span className="kpi-label">{label}</span>
        <span className="kpi-value">{value}</span>
        {sub && <span className="kpi-sub">{sub}</span>}
      </div>
    </motion.div>
  );
}

function ChartCard({ title, description, children, className = "" }) {
  return (
    <motion.div
      className={`chart-card ${className}`}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <h3 className="chart-title">{title}</h3>
      {description && <p className="chart-description">{description}</p>}
      {children}
    </motion.div>
  );
}

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      {label && <p className="tt-label">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>
          {p.name}: <strong>{formatter ? formatter(p.value) : p.value}</strong>
        </p>
      ))}
    </div>
  );
};

const CustomPieLabel = ({ cx, cy, midAngle, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const r  = outerRadius + 18;
  const x  = cx + r * Math.cos((-midAngle * Math.PI) / 180);
  const y  = cy + r * Math.sin((-midAngle * Math.PI) / 180);
  return (
    <text x={x} y={y} fill="var(--text-secondary)" textAnchor="middle" fontSize={11}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

/* ─────────────────── MAIN COMPONENT ─────────────────── */
export default function AnalyticsPage() {
  const navigate   = useNavigate();
  const { token }  = useAuth();

  const [leads,      setLeads]      = useState([]);
  const [properties, setProperties] = useState([]);
  const [deals,      setDeals]      = useState([]);
  const [payments,   setPayments]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [lastFetch,  setLastFetch]  = useState(null);
  const [userRole,   setUserRole]   = useState(null);
  const [userId,     setUserId]     = useState(null);

  // Extract user role and ID from token
  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      setUserRole(payload.role);
      // JWT stores user ID as "id", not "_id"
      setUserId(payload.id || payload._id);
    } catch (e) {
      console.error("Error decoding token:", e);
    }
  }, [token]);

  /* ── FETCH ── */
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [lRes, pRes, dRes, pyRes] = await Promise.all([
        api.get("/leads/all"),
        api.get("/properties"),
        api.get("/deals"),
        api.get("/payments"),
      ]);
      let leadsData      = Array.isArray(lRes.data)  ? lRes.data  : lRes.data.data  || [];
      let propertiesData = Array.isArray(pRes.data)  ? pRes.data  : pRes.data.data  || [];
      let dealsData      = Array.isArray(dRes.data)  ? dRes.data  : dRes.data.data  || [];
      let paymentsData   = Array.isArray(pyRes.data) ? pyRes.data : pyRes.data.data || [];

      // If agent, filter to their own data only
      // Backend already filters leads/deals by role, but properties & payments are unfiltered
      if (userRole === "agent" && userId) {
        // Leads: assigned to or created by this agent
        leadsData = leadsData.filter(l =>
          l.assignedTo?._id === userId || l.assignedTo === userId ||
          l.createdBy?._id === userId  || l.createdBy === userId
        );
        // Deals: created by this agent
        dealsData = dealsData.filter(d =>
          d.createdBy?._id === userId || d.createdBy === userId
        );
        // Properties: only those linked to the agent's leads
        const linkedPropertyIds = new Set(
          leadsData.map(l => l.propertyId?._id || l.propertyId).filter(Boolean).map(String)
        );
        propertiesData = propertiesData.filter(p => linkedPropertyIds.has(String(p._id)));
        // Payments: only those for the agent's deals
        const agentDealIds = new Set(dealsData.map(d => String(d._id)));
        paymentsData = paymentsData.filter(p =>
          agentDealIds.has(String(p.dealId?._id || p.dealId))
        );
      }

      setLeads(leadsData);
      setProperties(propertiesData);
      setDeals(dealsData);
      setPayments(paymentsData);
      setLastFetch(new Date());
    } catch (err) {
      console.error("Analytics fetch error", err);
      if (err.response?.status === 401) navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token || !userRole || (userRole === "agent" && !userId)) return;
    fetchAll();
  }, [token, userRole, userId]);

  /* ─────────────── DERIVED METRICS ─────────────── */
  const metrics = useMemo(() => {
    /* KPIs */
    const totalPipeline    = deals.reduce((s, d) => s + (d.dealValue || 0), 0);
    const totalReceived    = payments.filter(p => p.status === "Received").reduce((s, p) => s + p.amount, 0);
    const totalCommission  = deals.reduce((s, d) => s + (d.commissionAmout || 0), 0);
    const wonDeals         = deals.filter(d => d.stage === "closed_won");
    const lostDeals        = deals.filter(d => d.stage === "closed_lost");
    // Win rate = closed/won deals out of total leads (lead conversion rate)
    const winRate          = pct(wonDeals.length, leads.length);
    const avgDealValue     = deals.length ? Math.round(totalPipeline / deals.length) : 0;
    const hotLeads         = leads.filter(l => l.interest_level === "hot").length;
    const availableProps   = properties.filter(p => p.status === "available").length;

    /* Deal pipeline */
    const stageOrder = ["negotiation", "documentation", "payment_pending", "closed_won", "closed_lost"];
    const stageLabelMap = {
      negotiation: "Negotiation", documentation: "Documentation",
      payment_pending: "Pmt Pending", closed_won: "Won", closed_lost: "Lost",
    };
    const stageCounts = countBy(deals, "stage");
    const pipelineData = stageOrder.map(s => ({
      name:  stageLabelMap[s],
      value: stageCounts[s] || 0,
      fill:  STAGE_COLORS[s],
    }));

    /* Lead sources */
    const sourceCounts  = countBy(leads, "source");
    const sourceData    = toChartArr(sourceCounts, {
      website: "Website", facebook: "Social", google: "Google",
      walkin: "Walk-in", referral: "Referral", whatsapp: "WhatsApp", other: "Other",
    }).sort((a, b) => b.value - a.value);

    /* Lead status */
    const leadStatusCounts = countBy(leads, "status");
    const leadStatusData   = toChartArr(leadStatusCounts, {
      new: "New", contacted: "Contacted", interested: "Interested",
      follow_up: "Follow-up", matured: "Matured", closed: "Closed", lost: "Lost",
    });

    /* Interest level */
    const interestCounts = countBy(leads.filter(l => l.interest_level), "interest_level");
    const interestData   = toChartArr(interestCounts, { hot: "Hot", warm: "Warm", cold: "Cold" });

    /* Lead type */
    const leadTypeCounts = countBy(leads.filter(l => l.lead_type), "lead_type");
    const leadTypeData   = toChartArr(leadTypeCounts, { buyer: "Buyer", renter: "Tenant", seller: "Seller" });

    /* Payment status */
    const pyStatusCounts = countBy(payments, "status");
    const pyStatusData   = toChartArr(pyStatusCounts);

    /* Payment method */
    const pyMethodCounts = countBy(payments, "paymentMethod");
    const pyMethodData   = toChartArr(pyMethodCounts).sort((a, b) => b.value - a.value);

    /* Monthly revenue (last 6 months) */
    const now = new Date();
    const monthlyMap = {};
    for (let i = 5; i >= 0; i--) {
      const d   = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString("default", { month: "short", year: "2-digit" });
      monthlyMap[key] = { name: label, received: 0, pending: 0 };
    }
    payments.forEach(p => {
      const d   = new Date(p.paymentDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!monthlyMap[key]) return;
      if (p.status === "Received") monthlyMap[key].received += p.amount;
      else if (p.status === "Pending") monthlyMap[key].pending += p.amount;
    });
    const monthlyData = Object.values(monthlyMap);

    /* Property category */
    const propCatCounts = countBy(properties, "propertyCategory");
    const propCatData   = toChartArr(propCatCounts, { residential: "Residential", commercial: "Commercial" });

    /* Property status */
    const propStatusCounts = countBy(properties, "status");
    const STATUS_FILL = ["#22C55E","#F59E0B","#F43F5E","#06B6D4","#64748B","#F97316","#3B82F6"];
    const propStatusData   = toChartArr(propStatusCounts, {
      available: "Available", under_offer: "Under Offer",
      sold: "Sold", rented: "Rented", unavailable: "Unavailable",
      under_construction: "Under Const.", other: "Other",
    }).sort((a, b) => b.value - a.value)
      .map((d, i) => ({ ...d, fill: STATUS_FILL[i % STATUS_FILL.length] }));

    /* Property listing type — always show both bars even if count is 0 */
    const propListingCounts = countBy(properties, "listingType");
    const propListingData = [
      { name: "For Sale", value: propListingCounts["sale"]    || propListingCounts["For Sale"]    || 0, fill: "#6366F1" },
      { name: "For Rent", value: propListingCounts["rent"]    || propListingCounts["For Rent"]    || 0, fill: "#06B6D4" },
    ];

    /* Deal value by stage */
    const stageValueMap = {};
    deals.forEach(d => {
      const s = stageLabelMap[d.stage] || d.stage;
      stageValueMap[s] = (stageValueMap[s] || 0) + (d.dealValue || 0);
    });
    const stageValueData = stageOrder
      .map(s => ({ name: stageLabelMap[s], value: stageValueMap[stageLabelMap[s]] || 0, fill: STAGE_COLORS[s] }))
      .filter(d => d.value > 0);

    return {
      totalPipeline, totalReceived, totalCommission, winRate, avgDealValue,
      hotLeads, availableProps, wonDeals: wonDeals.length, lostDeals: lostDeals.length,
      pipelineData, sourceData, leadStatusData, interestData, leadTypeData,
      pyStatusData, pyMethodData, monthlyData,
      propCatData, propStatusData, propListingData, stageValueData,
    };
  }, [leads, properties, deals, payments]);

  /* ─────────────── RENDER ─────────────── */
  if (loading) {
    return (
      <div className="analytics-page">
        <div className="analytics-loading">
          <RefreshCw size={28} className="spin" />
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  const PIE_COLORS_STATUS   = ["#22C55E", "#F59E0B", "#F43F5E"];
  const PIE_COLORS_INTEREST = [C.rose, C.amber, C.blue];
  const PIE_COLORS_CAT      = [C.purple, C.teal];
  const PIE_COLORS_LISTING  = [C.indigo, C.cyan];
  const PIE_COLORS_LTYPE    = [C.blue, C.purple, C.teal];

  return (
    <div className="analytics-page">
      <div className="analytics-container">

        {/* ── Page Header ── */}
        <div className="analytics-header">
          <div>
            <h1>Analytics {userRole === "agent" && <span style={{ fontSize: "0.6em", color: "#3B82F6", fontWeight: 500 }}>· Your Data</span>}</h1>
            <p className="analytics-sub">
              {userRole === "agent"
                ? "Your assigned leads, deals, and linked activities"
                : "Live overview across leads, properties, deals & payments"}
            </p>
          </div>
          <button className="refresh-btn" onClick={fetchAll}>
            <RefreshCw size={15} />
            Refresh
            {lastFetch && (
              <span className="refresh-time">
                {lastFetch.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </button>
        </div>

        {/* ══════════════════════════════════
            SECTION 1 — KPI CARDS
        ══════════════════════════════════ */}
        <div className="kpi-grid">
          <KpiCard icon={TrendingUp}   label="Total Track Value" value={fmt(metrics.totalPipeline)}  sub={`${deals.length} deals`}              color={C.purple} delay={0}    />
          <KpiCard icon={CreditCard}   label="closed deals value"     value={fmt(metrics.totalReceived)}  sub={`${payments.filter(p=>p.status==="Received").length} payments`} color={C.green}  delay={0.05} />
          <KpiCard icon={Award}        label="Total Commission"      value={fmt(metrics.totalCommission)} sub="across all deals"                   color={C.amber}  delay={0.1}  />
          <KpiCard icon={Percent}      label="Win Rate"              value={`${metrics.winRate}%`}       sub={`${metrics.wonDeals} won / ${leads.length} leads`} color={C.teal}  delay={0.15} />
          <KpiCard icon={Users}        label="Total Leads"           value={leads.length}               sub={`${metrics.hotLeads} hot leads`}      color={C.rose}   delay={0.2}  />
          <KpiCard icon={Home}         label="Listed Properties"     value={properties.length}          sub={`${metrics.availableProps} available`} color={C.blue}   delay={0.25} />
          <KpiCard icon={CheckCircle}  label="Deals Won"             value={metrics.wonDeals}           sub={`Avg ${fmt(metrics.avgDealValue)}`}    color={C.green}  delay={0.3}  />
          <KpiCard icon={AlertTriangle} label="Overdue Payments"     value={payments.filter(p=>p.status==="Overdue").length} sub="need attention" color={C.orange} delay={0.35} />
        </div>

        {/* ══════════════════════════════════
            SECTION 2 — DEALS
        ══════════════════════════════════ */}
        <div className="section-label">Deal Pipeline</div>
        <div className="charts-row charts-row-2">

          {/* Deal count by stage */}
          <ChartCard title="Deals by Stage" description="Number of deals at each stage in the current pipeline.">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={metrics.pipelineData} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Deals" radius={[6, 6, 0, 0]}>
                  {metrics.pipelineData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Deal value by stage */}
          <ChartCard title="Deal Value by Stage (₹)" description="Total monetary value of deals grouped by their current pipeline stage.">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={metrics.stageValueData} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={v => fmt(v)} tick={{ fontSize: 10, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip formatter={fmt} />} />
                <Bar dataKey="value" name="Value" radius={[6, 6, 0, 0]}>
                  {metrics.stageValueData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* ══════════════════════════════════
            SECTION 3 — PAYMENTS
        ══════════════════════════════════ */}
        <div className="section-label">Payments</div>
        <div className="charts-row charts-row-3">

          {/* Monthly revenue */}
          <ChartCard title="Monthly Revenue (Last 6 Months)" description="Received vs pending payment amounts over the last 6 months." className="span-2">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={metrics.monthlyData}>
                <defs>
                  <linearGradient id="gReceived" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={C.green} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={C.green} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gPending" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={C.amber} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={C.amber} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={v => fmt(v)} tick={{ fontSize: 10, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip formatter={fmt} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="received" name="Received" stroke={C.green} fill="url(#gReceived)" strokeWidth={2} dot={{ r: 3, fill: C.green }} />
                <Area type="monotone" dataKey="pending"  name="Pending"  stroke={C.amber} fill="url(#gPending)"  strokeWidth={2} dot={{ r: 3, fill: C.amber }} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Payment status donut */}
          <ChartCard title="Payment Status" description="Breakdown of all payments by current status — received, pending, or overdue.">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={metrics.pyStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                  dataKey="value" nameKey="name" labelLine={false} label={CustomPieLabel}>
                  {metrics.pyStatusData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS_STATUS[i % PIE_COLORS_STATUS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Payment methods bar */}
        <div className="charts-row charts-row-1">
          <ChartCard title="Payments by Method" description="How clients are paying — cash, cheque, bank transfer, UPI, etc.">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={metrics.pyMethodData} layout="vertical" barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} width={90} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Payments" fill={C.indigo} radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* ══════════════════════════════════
            SECTION 4 — LEADS
        ══════════════════════════════════ */}
        <div className="section-label">Leads</div>
        <div className="charts-row charts-row-3">

          {/* Lead source horizontal bar */}
          <ChartCard title="Lead Sources" description="Which channels are generating the most leads — website, social, referral, walk-in, etc." className="span-2">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={metrics.sourceData} layout="vertical" barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} width={70} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Leads" radius={[0, 6, 6, 0]}>
                  {metrics.sourceData.map((_, i) => (
                    <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Lead interest level */}
          <ChartCard title="Interest Level" description="Distribution of lead interest — hot leads are ready to buy, warm are considering, cold need nurturing.">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={metrics.interestData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                  dataKey="value" nameKey="name" labelLine={false} label={CustomPieLabel}>
                  {metrics.interestData.map((entry, i) => (
                    <Cell key={i} fill={INTEREST_COLORS[entry.name?.toLowerCase()] || PIE_COLORS_INTEREST[i]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div className="charts-row charts-row-2">

          {/* Lead status donut */}
          <ChartCard title="Lead Status" description="Current progress of leads across the sales funnel — new, contacted, interested, matured, or closed.">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={metrics.leadStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                  dataKey="value" nameKey="name" labelLine={false} label={CustomPieLabel}>
                  {metrics.leadStatusData.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS_LEAD[Object.keys(STATUS_COLORS_LEAD)[i]] || SOURCE_COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Lead type */}
          <ChartCard title="Lead Type" description="Type of leads in the pipeline — buyers looking to purchase, tenants for rental, or sellers listing property.">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={metrics.leadTypeData} cx="50%" cy="50%" outerRadius={85}
                  dataKey="value" nameKey="name" labelLine={false} label={CustomPieLabel}>
                  {metrics.leadTypeData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS_LTYPE[i % PIE_COLORS_LTYPE.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* ══════════════════════════════════
            SECTION 5 — PROPERTIES
        ══════════════════════════════════ */}
        <div className="section-label">Properties</div>
        <div className="charts-row charts-row-3">

          {/* Property status bar */}
          <ChartCard title="Property Status" description="Count of properties by availability — available, under offer, sold, rented, or under construction." className="span-2">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={metrics.propStatusData} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Properties" radius={[6, 6, 0, 0]} isAnimationActive={false} fill="#22C55E">
                  {metrics.propStatusData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Property category donut */}
          <ChartCard title="Category Split" description="Residential vs commercial properties in the portfolio.">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={metrics.propCatData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                  dataKey="value" nameKey="name" labelLine={false} label={CustomPieLabel}>
                  {metrics.propCatData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS_CAT[i % PIE_COLORS_CAT.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Listing type */}
        <div className="charts-row charts-row-1">
          <ChartCard title="Listing Type (Sale vs Rent)" description="How the property portfolio splits between properties listed for outright sale vs rental.">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={metrics.propListingData} barCategoryGap="55%" barSize={80}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 13, fill: "var(--text-secondary)", fontWeight: 500 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Properties" radius={[8, 8, 0, 0]} isAnimationActive={false} minPointSize={4}>
                  {metrics.propListingData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display: "flex", justifyContent: "center", gap: "24px", marginTop: "6px" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "var(--text-secondary)" }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: "#6366F1", display: "inline-block" }} />
                For Sale: <strong style={{ color: "var(--text-primary)" }}>{metrics.propListingData[0]?.value ?? 0}</strong>
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "var(--text-secondary)" }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: "#06B6D4", display: "inline-block" }} />
                For Rent: <strong style={{ color: "var(--text-primary)" }}>{metrics.propListingData[1]?.value ?? 0}</strong>
              </span>
            </div>
          </ChartCard>
        </div>

      </div>
    </div>
  );
}